import { createSelector } from "reselect";
import i18n from "~/i18n";
import { getFavoriteTeamIds, getHasFavoriteTeams } from "~/store/selectors/selector.favorites";
import { isOwner, isPowerUser } from "~/utils/userHelper";
import { groupUsersByCompany, sortCompare } from "~/utils/utils.jsx";
import { getCompaniesArray } from "./selector.companies.jsx";
import { getProjectMembersById } from "./selector.projects.jsx";
import {
  getLoggedUser,
  getAllUsers,
  getActiveOwnersAndMembers,
  getAllUsersIdsNamesArray,
  getActiveUsers,
  getLoggedUserId,
} from "./selector.users.jsx";

const getFirstArg = (state, arg) => arg;

export const getAllTeams = (state) => state.teams;

export const getAllTeamsArray = createSelector(getAllTeams, (teams) => Object.values(teams));

export const getTeamById = createSelector(getAllTeams, getFirstArg, (teams, id) => teams[id]);

export const getNonEmptyTeams = createSelector(getAllTeamsArray, (teams) =>
  teams?.filter((team) => team.members && team.members.length)
);

export const getVisibleTeams = createSelector(
  getAllTeamsArray,
  getAllUsers,
  getLoggedUser,
  (teams, allUsers, loggedUser) => {
    return teams
      ?.filter((t) => {
        if (loggedUser.isOwner()) {
          return true;
        }

        if (t?.members?.length < 1) {
          return true;
        }

        return !!t?.members?.some((id) => {
          return allUsers.has(`${id}`);
        });
      })
      .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));
  }
);

export const getVisibleTeamIds = createSelector(getVisibleTeams, (visibleTeams) => visibleTeams.map((t) => t?.id));

export const getFavoriteAndUserAssociatedTeamIds = createSelector(
  getVisibleTeams,
  (state) => getFavoriteTeamIds(state),
  getLoggedUserId,
  (visibleTeams, favoriteTeams, loggedUserId) =>
    visibleTeams
      .filter((team) => favoriteTeams.includes(team.id) || team.members.includes(loggedUserId))
      .map((team) => team.id)
);

export const getVisibleTeamsGrouped = createSelector(
  getVisibleTeams,
  (state, teamIds) => teamIds,
  (state) => getFavoriteTeamIds(state),
  getHasFavoriteTeams,
  (teams, teamIds, favoriteTeamIds, hasFavoriteTeams) => {
    const filteredTeams = teams.filter((team) => {
      return teamIds.includes(team.id);
    });
    const groupedTeams = filteredTeams.reduce(
      (acc, team) => {
        if (favoriteTeamIds?.includes(team.id)) {
          acc.favorite.push(team);
        } else {
          acc.other.push(team);
        }
        return acc;
      },
      { favorite: [], other: [] }
    );
    return hasFavoriteTeams
      ? {
          favorite: groupedTeams.favorite
            .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()))
            .map((team) => team.id),
          other: groupedTeams.other
            .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()))
            .map((team) => team.id),
        }
      : { all: teamIds };
  }
);

export const canManageTeam = createSelector(
  (state, teamId) => getTeamById(state, teamId),
  getAllUsersIdsNamesArray,
  getLoggedUser,
  (team, allUsers, loggedUser) => {
    if (isOwner(loggedUser)) {
      return true;
    }

    return !!(
      isPowerUser(loggedUser) &&
      team?.created_by_id === loggedUser?.id &&
      team?.members?.every((userId) => allUsers?.find((u) => u.id === userId))
    );
  }
);

export const getLoggedUserTeams = createSelector(getAllTeamsArray, getLoggedUser, (teams, user) => {
  return teams?.filter((team) => team.members.some((id) => id === user?.id));
});

export const makeGetTeamsByUserId = () => {
  return createSelector(
    (state, userId) => userId,
    getAllTeamsArray,
    (userId, teams) => {
      return teams?.filter((team) => team.members.some((id) => id === userId)).map((team) => team.name);
    }
  );
};

export const makeTeamsByProjectMembersSelector = () => {
  return createSelector(getProjectMembersById, getAllTeamsArray, (projectMembers, teams) =>
    teams?.filter((team) => {
      return team.members.some((teamMemberId) => {
        return projectMembers.some((projectMemberId) => projectMemberId === teamMemberId);
      });
    })
  );
};

export const makeTeamMentionsByUserIdsSelector = (userIds) => {
  return createSelector(getAllTeamsArray, getAllUsers, getLoggedUser, (allTeams, allUsers, loggedUser) => {
    const teams = allTeams?.filter((team) => {
      return team?.members?.some((teamMemberId) => {
        return userIds.some((projectMemberId) => {
          return projectMemberId === teamMemberId && teamMemberId !== loggedUser.id;
        });
      });
    });

    return teams?.map((team) => {
      const membersInvited = team.members.filter((memberId) => userIds.indexOf(memberId) > -1);

      const getTooltipText = () => {
        const membersInvitedCopy = [...membersInvited];
        const loggedUserIndex = membersInvited.indexOf(loggedUser.id);

        if (loggedUserIndex > 0) {
          membersInvitedCopy.splice(loggedUserIndex, 1);
          membersInvitedCopy.unshift(loggedUser.id);
        }

        if (membersInvited.length > 5) {
          membersInvitedCopy.splice(5);
        }

        let memberNames = membersInvitedCopy
          .map((memberId) => {
            return memberId === loggedUser.id ? i18n.t("You") : allUsers.get(`${memberId}`)?.getName();
          })
          .join(", ");

        if (membersInvited.length > 5) {
          memberNames = `${memberNames} + ${membersInvited.length - membersInvitedCopy.length}`;
        }

        return memberNames;
      };

      return {
        type: "team",
        avatar_url: team.avatar_url ? team.avatar_url.replace(`--SIZE--`, 20) : "",
        name: team.name,
        members_count: team.members.length,
        members_invited: membersInvited.length,
        member_ids: membersInvited,
        tooltip_text: getTooltipText(),
      };
    });
  });
};

export const makeGetTeamUsers = (teamId) => {
  return createSelector(
    (state) => getTeamById(state, teamId),
    getActiveUsers,
    (team, users) => {
      if (!team) {
        return [];
      }

      return team?.members
        ?.reduce((acc, id) => {
          const user = users?.get(`${id}`);
          if (user) {
            acc.push({
              id: user.get("id"),
              name: user.get("display_name"),
              avatarUrl: user.get("avatar_url"),
            });
          }
          return acc;
        }, [])
        .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));
    }
  );
};

export const makeGetTeamUsersSortedByName = (teamId) => {
  return createSelector(
    (state) => getTeamById(state, teamId),
    getActiveUsers,
    (team, users) => {
      if (!team) {
        return [];
      }

      return team?.members
        ?.reduce((acc, id) => {
          const user = users?.get(`${id}`);
          if (user) {
            acc.push({
              id: user.get("id"),
              display_name: user.get("display_name"),
              email: user.get("email"),
              avatar_url: user.get("avatar_url"),
              [`class`]: user.get("class"),
              custom_permissions: user.get("custom_permissions"),
              is_email_at_example: user.get("is_email_at_example"),
            });
          }
          return acc;
        }, [])
        .sort((a, b) => sortCompare(a.display_name.toLowerCase(), b.display_name.toLowerCase()));
    }
  );
};

export const getOptionsForTeamSelect = createSelector(
  getActiveOwnersAndMembers,
  getCompaniesArray,
  (activeOwnersAndMembers, companiesArray) => {
    const users = activeOwnersAndMembers?.map((user) => ({
      id: user.id,
      name: user.display_name,
      image: user.avatar_url.replace("--SIZE--", "24"),
      class: user.class,
    }));

    const companies = companiesArray?.filter((c) => c.members.length > 0);

    return groupUsersByCompany(companies, users);
  }
);

export const getTeamsByIds = createSelector(
  getAllTeams,
  (_state, ids) => ids,
  (teams, ids) => {
    return ids
      .map((id) => teams[id])
      .filter(Boolean)
      .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));
  }
);
